// Direct imports
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { RawTexture } from "@babylonjs/core/Materials/Textures/rawTexture";
import { Material } from "@babylonjs/core/Materials/material";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import "@babylonjs/core/Meshes/Builders/planeBuilder"; // Side effect for CreatePlane
import { Graphic } from "./graphic";
import { Logger } from '../utils/Logger';
import { ASSET_PATIENCE_MS } from '../utils/AssetPatience';
import { ImageData } from '../scenedata/behaviors/imagedata';
import { Orientation } from '../scenedata/common';
import { ValidationError } from '../scenedata/behaviordata';
import { decodeGeoTiffTexture } from '../utils/GeoTiffLoader';
import { isDecodedImageExtension, isAlphaCapableImageSrc, isPngBlob } from '../utils/AssetTypes';
import { Utility } from '../utils/Utility';

// 2024
// Author: Aaron Post

export class Image extends Graphic<ImageData> {
    static readonly behaviorType = "Image" as const;
    private quad: Mesh | null = null;
    public aspectRatio: number = 1;
    public orientation: Orientation = Orientation.Vertical;
    private texture: Texture | RawTexture | null = null;
    /** Bounded-wait guard for loadStandardImage (see there). */
    private _settleGuard: ReturnType<typeof setTimeout> | null = null;

    protected getFitQuad(): Mesh | null {
        return this.quad;
    }

    protected getFitMode(): 'contain' | 'cover' {
        return this._resolved.fit ?? 'contain';
    }

    protected getCornerRadius(): number {
        return this._resolved.cornerRadius ?? 0;
    }

    protected getPill(): boolean {
        return this._resolved.pill === true;
    }

    protected getBack(): 'none' | 'picture' | 'color' {
        return this._resolved.back ?? 'none';
    }

    protected getBackColor(): string {
        return this._resolved.backColor ?? '#FFFFFF';
    }

    /**
     * Validate image data.
     */
    protected validate(data: ImageData): ValidationError[] {
        const errors: ValidationError[] = [];

        // Validate aspect ratio if present
        if (data.aspect !== undefined) {
            if (typeof data.aspect !== 'number' || data.aspect <= 0) {
                errors.push({
                    field: 'aspect',
                    message: 'Aspect ratio must be a positive number',
                    code: 'INVALID_ASPECT'
                });
            }
        }

        this.validateFit(data.fit, errors);
        this.validateCornerRadius(data.cornerRadius, errors);
        this.validatePill(data.pill, errors);
        this.validateBack(data.back, errors);
        this.validateBackColor(data.backColor, errors);

        return errors;
    }

    protected async applyCommit(): Promise<void> {
        this.applyOrientation();
    }

    /**
     * Apply the current orientation to the quad mesh.
     */
    private applyOrientation(): void {
        if (!this.quad) return;

        this.orientation = this._resolved.orientation ?? Orientation.Vertical;
        if (this.orientation === Orientation.Horizontal) {
            // Face up (180° around Y, then +90° around X)
            this.quad.rotation = new Vector3(Math.PI / 2, Math.PI, 0);
        } else {
            // Face forward (180° around Y)
            this.quad.rotation = new Vector3(0, Math.PI, 0);
        }
        this.onOrientationChangedObservable.notifyObservers(this.orientation);
        this.updateQuadFit();
    }

    /**
     * Adopt the authored aspect, if the data carries one.
     *
     * A scene that has been through applyTextureToMaterial once records the
     * measured aspect (see there), so a reload knows the shape before the
     * texture arrives — and keeps it if the texture never arrives at all.
     * Video does the same at the top of its own load(); Image had no such
     * path, so it entered every load claiming 1:1. Mirrors Video in the other
     * direction too: the authored number is only a starting point, the
     * measured one below still wins once it exists.
     */
    private adoptAuthoredAspect(): void {
        const authored = this._resolved.aspect;
        if (typeof authored === 'number' && authored > 0) {
            this.aspectRatio = authored;
            this.aspectKnown = true;
        }
    }

    load(): Promise<void> {
        const src = this.sceneObject.src;
        const name = this.sceneObject.name;
        Logger.debug(`loading image ${name}`);
        this.adoptAuthoredAspect();

        if (!src) {
            Logger.debug(`[Image] No src specified for ${name}`);
            return Promise.resolve();
        }

        // GeoTIFF files need special decoding — browsers can't display them natively
        if (isDecodedImageExtension(Utility.getFileExtensionFromSrc(src))) {
            return this.loadGeoTiff(src, name);
        }

        return this.loadStandardImage(src, name);
    }

    /**
     * Load a GeoTIFF/TIFF file: decode with geotiff.js, create a RawTexture,
     * and store the raw raster data for processing/analysis.
     */
    private async loadGeoTiff(src: string, name: string): Promise<void> {
        this.setupQuad(name);

        const material = new StandardMaterial(`${name}_mat`, this.scene);
        const blob = this.resources.getAsset(src);
        if (!blob) {
            Logger.warn(`[Image] GeoTIFF "${name}": no blob found for "${src}"`);
            material.disableLighting = true;
            if (this.quad) this.quad.material = material;
            return;
        }

        try {
            const { rgba, width, height, isGeoReferenced } = await decodeGeoTiffTexture(blob);

            Logger.debug(`[Image] GeoTIFF "${name}" decoded: ${width}x${height}, geoReferenced=${isGeoReferenced}`);

            // Create a RawTexture from the decoded RGBA data
            const rawTexture = RawTexture.CreateRGBATexture(
                rgba,
                width,
                height,
                this.scene,
                false, // generateMipMaps
                false, // invertY
                Texture.BILINEAR_SAMPLINGMODE,
            );
            this.texture = rawTexture;

            this.applyTextureToMaterial(rawTexture, material, name, width, height, false);

            // Geo-referenced TIFFs (DEMs, satellite imagery) default to face-up
            if (isGeoReferenced && this._resolved.orientation !== Orientation.Horizontal) {
                await this.commit({ orientation: Orientation.Horizontal });
            }
        } catch (error) {
            Logger.warn(`[Image] GeoTIFF decode error for "${name}": ${error instanceof Error ? error.message : String(error)}`);
            material.disableLighting = true;
            if (this.quad) this.quad.material = material;
        }
    }

    /**
     * Standard image load path (PNG, JPG, WebP, etc.) — unchanged original logic.
     */
    private loadStandardImage(src: string, name: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // Settle guarantee: a texture can fire neither onLoad nor onError
            // (NullEngine, exotic loaders) — and this promise is awaited by
            // scene/object creation, so a dangling load parked entire headless
            // runs on an idle event loop. Resolve after a bounded wait.
            // Captured per-call so a (hypothetical) re-entrant load() can't
            // cross-clear another call's timer; mirrored to the instance
            // field only so onDetach can cancel an abandoned load.
            const guard = setTimeout(() => {
                if (this._settleGuard === guard) this._settleGuard = null;
                Logger.warn(`[Image] "${name}" load did not settle in ${ASSET_PATIENCE_MS / 1000}s — continuing without texture confirmation`);
                resolve();
            }, ASSET_PATIENCE_MS);
            this._settleGuard = guard;
            const settled = (fn: () => void) => {
                clearTimeout(guard);
                if (this._settleGuard === guard) this._settleGuard = null;
                fn();
            };
            try {
                this.setupQuad(name);

                // Note: Transform is already applied to sceneObject.transform by the plugin
                // Do NOT call applyTransform here - it would double-apply the transform

                const material = new StandardMaterial(`${name}_mat`, this.scene);

                // Error handler — reject the promise so loading isn't blocked
                const onTextureError = (_message?: string, _exception?: unknown) => {
                    Logger.warn(`[Image] Texture error for "${name}": ${_message}`);
                    // Still assign the (fallback) material so the quad isn't invisible
                    material.disableLighting = true;
                    if (this.quad) {
                        this.quad.material = material;
                    }
                    settled(resolve); // resolve (not reject) so other behaviors continue loading
                };

                // Load the texture — try cached blob first (covers both local
                // paths and HTTP URLs with a registered URL mapping), fall back
                // to loading from URL directly.
                const blob = src ? this.resources.getAsset(src) : undefined;
                Logger.debug(`[Image] "${name}" loading from ${blob ? 'blob' : 'URL'}`);
                if (blob) {
                    const blobUrl = URL.createObjectURL(blob);
                    this.texture = new Texture(blobUrl, this.scene, false, false, Texture.BILINEAR_SAMPLINGMODE, undefined, onTextureError);

                    // Clean up the blob URL after the texture is loaded
                    this.texture.onLoadObservable.addOnce(() => {
                        URL.revokeObjectURL(blobUrl);
                        Logger.debug(`Revoked object URL for blob in ${name}`);
                    });
                } else {
                    // Nothing cached: a folder document's relative src
                    // resolves against the document's own URL (spec §2.3).
                    const url = (src ? this.resources.resolveAssetUrl(src) : undefined) ?? src ?? "";
                    this.texture = new Texture(url, this.scene, false, false, Texture.BILINEAR_SAMPLINGMODE, undefined, onTextureError);
                }
                if(this.texture) {
                    const texture = this.texture;
                    const applyWhenReady = async () => {
                        try {
                            // Alpha when the src says PNG, or when the cached
                            // bytes are one whatever the src is called (a
                            // library reference names no format).
                            const enableAlpha = isAlphaCapableImageSrc(src)
                                || (blob !== undefined && await isPngBlob(blob));
                            if (this.texture !== texture) {
                                settled(resolve); // detached while the bytes were read
                                return;
                            }
                            const imageWidth = texture.getSize().width;
                            const imageHeight = texture.getSize().height;
                            this.applyTextureToMaterial(texture, material, name, imageWidth, imageHeight, enableAlpha);
                            settled(resolve);
                        } catch (error) {
                            settled(() => reject(error instanceof Error ? error : new Error(String(error))));
                        }
                    };
                    // A texture that is already ready (engine URL cache, fast
                    // same-origin load) has ALREADY notified its load
                    // observable — adding afterwards never fires, the material
                    // never receives the texture, and the quad renders as a
                    // grey slab. Check first, subscribe otherwise.
                    if (texture.isReady()) {
                        void applyWhenReady();
                    } else {
                        texture.onLoadObservable.addOnce(() => { void applyWhenReady(); });
                    }
                }
                else {
                    settled(() => reject(new Error("Image source element not found")));
                }
            } catch (error) {
                // Clear the guard on failure paths too — a promise settled by
                // rejection must not log a spurious "did not settle" warning
                // 10s later.
                settled(() => reject(error instanceof Error ? error : new Error(String(error))));
            }
        });
    }

    /** Create the quad mesh with flipped UVs and parent it to the SceneObject. */
    private setupQuad(name: string): void {
        this.quad = MeshBuilder.CreatePlane("IMAGE_PLANE", { size: 1 }, this.scene);

        // Flip UVs to compensate for the 180° Y rotation and texture coordinate
        // convention. This is done on the mesh geometry so that any material
        // (including Renderer material overrides) displays correctly without
        // needing texture uScale/vScale adjustments.
        const uvs = this.quad.getVerticesData("uv");
        if (uvs) {
            for (let i = 0; i < uvs.length; i += 2) {
                uvs[i] = 1 - uvs[i];       // flip U (compensates for 180° Y rotation)
                uvs[i + 1] = 1 - uvs[i + 1]; // flip V (compensates for texture Y convention)
            }
            this.quad.setVerticesData("uv", uvs);
        }

        if (this.sceneObject && this.sceneObject.transform) {
            Logger.debug("Setting quad as child of SceneObject transform");
            this.quad.name = `${name}_mesh`;
            this.applyOrientation();
            this.sceneObject.addChild(this.quad);
            this.watchNodeScaleForFit();
        }
    }

    /** Apply a loaded texture to the material and quad, updating aspect ratio. */
    private applyTextureToMaterial(
        texture: Texture | RawTexture,
        material: StandardMaterial,
        name: string,
        imageWidth: number,
        imageHeight: number,
        enableAlpha: boolean,
    ): void {
        this.aspectRatio = imageWidth / imageHeight;
        this.aspectKnown = true;
        Logger.debug(`[Image] "${name}" texture loaded, ${imageWidth}x${imageHeight}, aspect=${this.aspectRatio}`);

        // Runtime measurement, so _resolved only — same rule as Video (which
        // needs an explicit commitAspectWhenAvailable() to persist one).
        //
        // This used to write AUTHORED data too, which turned every load into a
        // permanent edit of the scene: whatever the loader happened to hand
        // back became the node's authored shape, and a save then baked it in.
        // The failure it caused is not hypothetical — an engine whose loader
        // reports a fixed square for every texture (NullEngine, used by the
        // headless export path) re-authored `aspect: 1` onto landscape posters,
        // and every later render letterboxed them square from data rather than
        // from anything anyone had seen. Authored data is the author's; the
        // runtime gets _resolved.
        this._resolved.aspect = this.aspectRatio;

        this.updateQuadFit();

        this.onAspectChangedObservable.notifyObservers(this.aspectRatio);

        material.emissiveTexture = texture;
        material.disableLighting = true;

        if (enableAlpha) {
            texture.hasAlpha = true;
            if ('getAlphaFromRGB' in texture) {
                texture.getAlphaFromRGB = false;
            }
            material.opacityTexture = texture;
            material.transparencyMode = Material.MATERIAL_ALPHABLEND;
        }

        if (this.quad) {
            Logger.debug(`Assigning material to quad for image ${name}`);
            this.quad.material = material;
        }
    }

    protected onDetach(): void {
        this.disposeFitWatcher();
        this.disposeBackCard();
        // A load abandoned by detach must not fire its 10s warning later.
        if (this._settleGuard) {
            clearTimeout(this._settleGuard);
            this._settleGuard = null;
        }

        // Dispose of the quad mesh
        if (this.quad) {
            this.quad = null;
        }

        // Dispose of the texture if it exists
        if (this.texture) {
            Logger.debug(`Disposing of the texture for image ${this.sceneObject.name}`);
            this.texture.dispose();
            this.texture = null;
        }
    }
}
